import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import { t } from '@aquilixshop/i18n';
import { ALIGN_OPTIONS, marginFields, visibilityField, weightField, } from '../_shared/blockFields.js';
import { marginStyle, num, readWeight, str, visibilityCss } from '../_shared/blockRender.js';
import { isThemeColorToken } from '../../theme/colorTokens.js';
import { REVIEWS_ANCHOR_ID, formatAverage, formatCount, } from '../../sections/_shared/reviewFormat.js';
import { initRatingPopover } from './popoverDismiss.js';
/**
 * Shrine `rating-stars` — config-by-config. The note is DECORATIVE (a setting, not a real aggregation):
 * `rating` (1–5, .1), star + background-star colours, `bg_stars_style` (full/outlined), a free `label`
 * text, `size`, `scroll_id`, plus the full Shrine "Styling" group (alignment, width, background
 * solid/gradient + opacity, border thickness/color/style, corner radius, drop shadow) + Visibility +
 * Margins.
 *
 * ID MAPPING (our id ← Shrine id, kept stable — never renamed): starColor←star_color,
 * bgStarsStyle←bg_stars_style, bgStarColor←bg_star_color, scrollId←scroll_id, bgType←bg_type,
 * bgColor←bg_color, bgGradient←bg_gradient (our `gradient` field type = Shrine `color_background`),
 * bgOpacity←bg_opacity, borderThickness←border_thickness, borderColor←border_color,
 * borderStyle←border_style, cornerRadius←corner_radius, enableShadow←enable_shadow,
 * shadowColor←shadow_color, shadowOpacity←shadow_opacity, shadowHorizontalOffset←shadow_horizontal_offset,
 * shadowVerticalOffset←shadow_vertical_offset, shadowBlur←shadow_blur, shadowSpread←shadow_spread.
 *
 * NOTE on conditional visibility: our `visibleWhen` predicate is equals/notEquals only (no numeric `>`).
 * The bg_* fields (gated on bg_type) and shadow_* fields (gated on the enableShadow boolean) carry a
 * faithful `visibleWhen`; the border_color/border_style/corner_radius fields, which Shrine gates on
 * `border_thickness > 0` (a numeric comparison we can't express), are left always-visible in the
 * inspector — the RENDER still honours thickness 0 (no border drawn). Shrine's orphaned
 * margin_bottom `visible_if section.settings.image_1_visibility…` is a copy-paste bug and is dropped.
 */
export const ratingBlockSchema = {
    type: 'rating',
    name: 'Rating stars',
    max: 1,
    fields: [
        visibilityField,
        /**
         * WHERE THE NOTE COMES FROM (feature 0066, default revisited 16/09/2026).
         *
         * `manual` is what this block always did: a number the seller picks, decoration. `app` reads the REAL
         * aggregate the Avis app computed for this product. `auto` — the DEFAULT — is `app` the moment the
         * product has a published review, and `manual` until then.
         *
         * The default was `manual`, on the reasoning that flipping every live store at once would wipe a
         * number they chose. That reasoning was wrong, and the founder said so: a page whose product HAS
         * 1 535 real reviews was still printing a typed `4.8` and no breakdown card, and the only way to the
         * truth was a setting nobody knew to open. `auto` never wipes anything — a product with no review
         * still prints the seller's number; a product WITH reviews stops contradicting them.
         */
        {
            id: 'source',
            type: 'select',
            label: 'Rating source',
            default: 'auto',
            options: [
                { value: 'auto', label: 'My real reviews, my number until there are any' },
                { value: 'app', label: 'My real reviews only' },
                { value: 'manual', label: 'A number I choose' },
            ],
            info: 'The stars show the average of the published reviews of this product as soon as it has one.',
        },
        {
            id: 'showCount',
            type: 'checkbox',
            label: 'Show the number of reviews',
            default: true,
            visibleWhen: { field: 'source', notEquals: 'manual' },
        },
        /**
         * THE BREAKDOWN CARD (16/09/2026, founder's reference shot).
         *
         * The stars under a product title say 4.9; the shopper's next question is always "out of how many, and
         * how lopsided?". Every review app answers it in the same place — a card that drops under the stars
         * with the five levels and their counts, and a button that takes the reader down to the reviews
         * themselves. So the stars become a control, and the number stops being a claim you have to take on
         * faith.
         *
         * Only offered on the LIVE note: a card of bars under a rating the seller typed by hand would be five
         * invented numbers, which is the one thing this block must never print.
         */
        {
            id: 'showBreakdown',
            type: 'checkbox',
            label: 'Open the breakdown when the stars are clicked',
            default: true,
            visibleWhen: { field: 'source', notEquals: 'manual' },
            info: 'A card with the five star levels and a button that scrolls down to the reviews.',
        },
        {
            id: 'rating',
            type: 'range',
            label: 'Rating',
            min: 1,
            max: 5,
            step: 0.1,
            default: 4.8,
            // Still the number the page prints while the product waits for its first published review.
            info: 'Used until this product has a published review of its own.',
            visibleWhen: { field: 'source', notEquals: 'app' },
        },
        {
            id: 'starColor',
            type: 'color',
            label: 'Stars color',
            // 0019: theme-driven by default (star token = #ffcc00) so a re-theme reflows; still overridable.
            default: { ref: 'theme', token: 'star' },
        },
        {
            id: 'bgStarsStyle',
            type: 'select',
            label: 'Background stars style',
            default: 'full',
            options: [
                { value: 'full', label: 'Full' },
                { value: 'outlined', label: 'Outlined' },
            ],
        },
        {
            id: 'bgStarColor',
            type: 'color',
            label: 'Empty stars color',
            // The underlay is only visible on the UNRATED fraction (0.1 star at 4.9) — a merchant read "background"
            // as "the star fill" and reported the setting as broken (founder, 2026-09-11).
            info: 'Only visible on the unrated part (0.1 star for a 4.9 rating). Lower the rating to see it.',
            default: { ref: 'custom', value: '#ececec' },
        },
        {
            id: 'label',
            type: 'text',
            label: 'Text',
            default: '(xxxx avis)',
            defaultKey: 'sticky.reviews_placeholder',
            maxLength: 60,
            // See LIVE NOTE, LIVE TEXT in the render: a typed count loses to the real one, so say it here
            // rather than let a seller wonder why their text is not the one on the page.
            info: 'Used while the note is a number you choose. With real reviews, the count is written for you.',
        },
        /**
         * The LABEL's colour — the stars had two settings and the text beside them had none, so the note
         * was stuck on the section's muted grey (founder, 2026-09-14).
         *
         * `default` is that same muted grey, and it stays the default on purpose: every live page already
         * draws this label, and shipping a hard colour would repaint all of them at once. A seller opts in.
         */
        {
            id: 'textColor',
            type: 'select',
            label: 'Text color',
            default: 'default',
            options: [
                { value: 'default', label: 'Default' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'textCustomColor',
            type: 'color',
            label: 'Custom text color',
            default: { ref: 'theme', token: 'ink' },
            visibleWhen: { field: 'textColor', equals: 'custom' },
        },
        {
            id: 'size',
            type: 'range',
            label: 'Text size',
            min: 10,
            max: 24,
            step: 1,
            default: 16,
            unit: 'px',
        },
        weightField,
        // 0059 — an anchor id, not prose: never AI-writable.
        {
            id: 'scrollId',
            type: 'text',
            label: 'Scroll ID',
            default: '',
            maxLength: 60,
            aiWritable: false,
        },
        // ── Styling ──────────────────────────────────────────────────────────────
        {
            id: 'alignment',
            type: 'select',
            label: 'Alignment',
            group: 'Styling',
            default: 'left',
            options: ALIGN_OPTIONS,
        },
        {
            id: 'width',
            type: 'select',
            label: 'Width',
            group: 'Styling',
            default: 'fit_content',
            options: [
                { value: 'fit_content', label: 'Fit content' },
                { value: 'full', label: 'Full' },
            ],
        },
        {
            id: 'bgType',
            type: 'select',
            label: 'Background',
            group: 'Styling',
            default: 'none',
            options: [
                { value: 'none', label: 'None' },
                { value: 'solid', label: 'Solid color' },
                { value: 'gradient', label: 'Gradient color' },
            ],
        },
        {
            id: 'bgColor',
            type: 'color',
            label: 'Background color',
            group: 'Styling',
            default: { ref: 'theme', token: 'accent-2' },
            visibleWhen: { field: 'bgType', equals: 'solid' },
        },
        {
            id: 'bgGradient',
            type: 'gradient',
            label: 'Background gradient',
            group: 'Styling',
            default: 'linear-gradient(135deg, rgba(232,220,255,1) 0%, rgba(255,220,232,1) 100%)',
            visibleWhen: { field: 'bgType', equals: 'gradient' },
        },
        {
            id: 'bgOpacity',
            type: 'range',
            label: 'Background opacity',
            group: 'Styling',
            min: 0,
            max: 100,
            step: 5,
            default: 100,
            unit: '%',
            visibleWhen: { field: 'bgType', notEquals: 'none' },
        },
        {
            id: 'borderThickness',
            type: 'range',
            label: 'Border thickness',
            group: 'Styling',
            min: 0,
            max: 8,
            step: 1,
            default: 0,
            unit: 'px',
        },
        {
            id: 'borderColor',
            type: 'color',
            label: 'Border color',
            group: 'Styling',
            default: { ref: 'custom', value: '#000000' },
        },
        {
            id: 'borderStyle',
            type: 'select',
            label: 'Border style',
            group: 'Styling',
            default: 'solid',
            options: [
                { value: 'solid', label: 'Solid' },
                { value: 'dashed', label: 'Dashed' },
            ],
        },
        {
            id: 'cornerRadius',
            type: 'range',
            label: 'Corner radius',
            group: 'Styling',
            min: 0,
            max: 100,
            step: 5,
            default: 30,
            unit: '%',
        },
        {
            id: 'enableShadow',
            type: 'checkbox',
            label: 'Enable shadow',
            group: 'Styling',
            default: false,
        },
        {
            id: 'shadowColor',
            type: 'color',
            label: 'Shadow color',
            group: 'Styling',
            default: { ref: 'custom', value: '#000000' },
            visibleWhen: { field: 'enableShadow', equals: true },
        },
        {
            id: 'shadowOpacity',
            type: 'range',
            label: 'Shadow opacity',
            group: 'Styling',
            min: 0,
            max: 100,
            step: 5,
            default: 15,
            unit: '%',
            visibleWhen: { field: 'enableShadow', equals: true },
        },
        {
            id: 'shadowHorizontalOffset',
            type: 'range',
            label: 'Shadow horizontal offset',
            group: 'Styling',
            min: -50,
            max: 50,
            step: 2,
            default: 0,
            visibleWhen: { field: 'enableShadow', equals: true },
        },
        {
            id: 'shadowVerticalOffset',
            type: 'range',
            label: 'Shadow vertical offset',
            group: 'Styling',
            min: -50,
            max: 50,
            step: 2,
            default: 10,
            visibleWhen: { field: 'enableShadow', equals: true },
        },
        {
            id: 'shadowBlur',
            type: 'range',
            label: 'Shadow blur',
            group: 'Styling',
            min: 0,
            max: 100,
            step: 2,
            default: 20,
            visibleWhen: { field: 'enableShadow', equals: true },
        },
        {
            id: 'shadowSpread',
            type: 'range',
            label: 'Shadow spread',
            group: 'Styling',
            min: -50,
            max: 50,
            step: 2,
            default: 0,
            visibleWhen: { field: 'enableShadow', equals: true },
        },
        ...marginFields(15, 15),
    ],
};
// Star SVG (Shrine viewBox 0 0 576 512). The overlay (coloured) is clipped to the rating width over the
// underlay (grey), so a fractional rating (4.8 → 96%) shows precisely.
const STAR_FULL = 'M259.3 17.8 194 150.2 47.9 171.4c-26.2 3.8-36.7 36.1-17.7 54.6l105.7 103-25 145.5c-4.5 26.3 23.2 46.4 46.4 33.7L288 439.6l130.7 68.7c23.2 12.1 50.9-7.4 46.4-33.7l-25-145.5 105.7-103c19-18.5 8.5-50.8-17.7-54.6L382 150.2 316.7 17.8c-11.7-23.6-45.6-23.7-57.4 0z';
const STAR_OUTLINE = 'M287.9 0c9.2 0 17.6 5.2 21.6 13.5l68.6 141.3 153.2 22.6c9 1.3 16.5 7.6 19.3 16.3s.5 18.1-5.9 24.5L433.6 433.9l26.4 156.6c1.5 9-2.2 18.1-9.7 23.5s-17.3 6-25.3 1.7l-137-73.2-137 73.2c-8 4.3-17.8 3.7-25.3-1.7s-11.2-14.5-9.7-23.5l26.4-156.6L17.4 218.7c-6.5-6.4-8.7-15.9-5.9-24.5s10.3-15 19.3-16.3l153.2-22.6L252.3 13.5C256.4 5.2 264.7 0 287.9 0zm.1 79L235.4 187.2c-3.5 7.1-10.2 12.1-18.1 13.3L99 217.9 184.9 303c5.5 5.5 8.1 13.3 6.8 21L170.4 443.7l105.2-56.2c7.1-3.8 15.6-3.8 22.6 0l105.2 56.2L382.2 324c-1.3-7.7 1.2-15.5 6.8-21l85.9-85.1L356.6 200.5c-7.9-1.2-14.6-6.1-18.1-13.3L288 79z';
function Stars({ fill, outline }) {
    const svg = { width: '1em', height: '1em', flexShrink: 0, fill };
    return (_jsx(_Fragment, { children: [0, 1, 2, 3, 4].map((i) => (_jsx("svg", { viewBox: "0 0 576 512", "aria-hidden": "true", style: { ...svg, marginInlineStart: i === 0 ? 0 : '0.125em' }, children: _jsx("path", { d: outline ? STAR_OUTLINE : STAR_FULL }) }, i))) }));
}
const JUSTIFY = { left: 'flex-start', center: 'center', right: 'flex-end' };
/** Convert a #rrggbb hex + 0–100 opacity into an rgba() string (SSR-safe, no canvas). */
function hexToRgba(hex, opacityPct) {
    const a = Math.max(0, Math.min(100, opacityPct)) / 100;
    // A theme token arrives as var(--aqx-theme-*); hex arithmetic cannot read it, so mix instead.
    if (hex.startsWith('var(')) {
        return a >= 1 ? hex : `color-mix(in srgb, ${hex} ${Math.round(a * 100)}%, transparent)`;
    }
    const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
    if (!m || !m[1])
        return hex;
    const int = parseInt(m[1], 16);
    const r = (int >> 16) & 255;
    const g = (int >> 8) & 255;
    const b = int & 255;
    return `rgba(${r}, ${g}, ${b}, ${a})`;
}
const RatingBlock = ({ block, context }) => {
    const bs = block.settings;
    const cls = `aqx-rt-${block.id}`;
    /**
     * The real note, when the seller asked for it AND this product has one. No reviews ⇒ the block renders
     * NOTHING: showing "0.0 ★" under a price is worse than showing no stars, and falling back to the
     * decorative number would print a note the shop cannot justify.
     */
    const source = str(bs.source, 'auto');
    const found = source !== 'manual' ? context.reviews : undefined;
    // One definition of "there is a note to show", used by the stars, the text and the card alike: a
    // `reviews` object that exists AND counts something. `count === 0` used to reach the render as a live
    // aggregate and print "0.0 ★".
    const live = found && found.count > 0 ? found : undefined;
    // The card's light dismiss (click outside / Escape / the CTA), bound once the island is live. Before
    // that — and on a page whose script never arrives — the native toggle alone still works.
    const popRef = useRef(null);
    useEffect(() => {
        const details = popRef.current;
        if (!details)
            return undefined;
        return initRatingPopover(details);
    });
    // `app` is the strict mode a seller opts into: the real note or NOTHING. `auto` never returns null —
    // it simply falls back to the number the seller typed, which is what the page printed before.
    if (source === 'app' && !live && context.renderMode !== 'edit')
        return null;
    const rating = live ? live.average : num(bs.rating, 4.8);
    const size = num(bs.size, 16);
    const star = resolveHex(bs.starColor, '#ffcc00');
    const bgStar = resolveHex(bs.bgStarColor, '#ececec');
    const bgOutline = bs.bgStarsStyle === 'outlined';
    const visibleStars = Math.ceil(rating) || 1;
    const overlayWidth = `calc(${rating} * 1em + ${visibleStars - 1} * 0.125em)`;
    /**
     * LIVE NOTE, LIVE TEXT. The typed label used to win over the real count, and that is precisely how a
     * page with 1 535 published reviews kept printing `4.8  ( 1535 reviews )` — one frozen sentence,
     * written before the app existed, sitting an inch above a card that said 4,9 and 1 535. A note that
     * comes from the reviews owns the text beside it too; a seller who wants their own wording picks
     * "a number I choose", where the field is theirs again.
     *
     * The text reads like the founder's reference — the average, then the count — in the READER's digits
     * and words: `4,9 (1 535 avis)` in French, `4.9 (1,535 reviews)` in English.
     */
    const liveText = live && bs.showCount !== false
        ? `${formatAverage(live.average, context.locale)} (${t(context, 'reviews.count', {
            count: live.count,
        })})`
        : '';
    const label = live ? liveText : str(bs.label);
    const weight = readWeight(bs);
    // 'default' = the section's muted text, exactly what this block drew before the setting existed.
    const labelColor = str(bs.textColor, 'default') === 'custom'
        ? resolveHex(bs.textCustomColor, 'var(--aqx-theme-ink)')
        : 'var(--aqx-scheme-muted)';
    const justify = JUSTIFY[str(bs.alignment, 'left')] ?? 'flex-start';
    const scrollId = str(bs.scrollId);
    const vis = visibilityCss(cls, bs.visibility);
    // ── Styling group → the inner "card" wrapping the stars+label. ──────────────
    const fullWidth = str(bs.width, 'fit_content') === 'full';
    const bgType = str(bs.bgType, 'none');
    const bgOpacity = num(bs.bgOpacity, 100);
    const borderThickness = num(bs.borderThickness, 0);
    const enableShadow = bs.enableShadow === true;
    // Corner radius (%) maps to a px-bounded radius so it reads as a pill at 100% without distorting.
    const cornerRadius = num(bs.cornerRadius, 30);
    const hasBox = bgType !== 'none' || borderThickness > 0 || enableShadow;
    const cardStyle = {
        display: 'flex',
        alignItems: 'center',
        width: fullWidth ? '100%' : 'fit-content',
    };
    if (bgType === 'solid') {
        cardStyle.background = hexToRgba(resolveHex(bs.bgColor, '#BFE3FF'), bgOpacity);
    }
    else if (bgType === 'gradient') {
        cardStyle.background = str(bs.bgGradient, 'linear-gradient(135deg, rgba(232,220,255,1) 0%, rgba(255,220,232,1) 100%)');
        if (bgOpacity < 100)
            cardStyle.opacity = bgOpacity / 100;
    }
    if (borderThickness > 0) {
        cardStyle.border = `${borderThickness}px ${str(bs.borderStyle, 'solid')} ${resolveHex(bs.borderColor, '#000000')}`;
    }
    if (hasBox) {
        cardStyle.borderRadius = `${cornerRadius}%`;
        cardStyle.padding = '0.5em 0.75em';
    }
    if (enableShadow) {
        const sx = num(bs.shadowHorizontalOffset, 0);
        const sy = num(bs.shadowVerticalOffset, 10);
        const sb = num(bs.shadowBlur, 20);
        const ss = num(bs.shadowSpread, 0);
        const sc = hexToRgba(resolveHex(bs.shadowColor, '#000000'), num(bs.shadowOpacity, 15));
        cardStyle.boxShadow = `${sx}px ${sy}px ${sb}px ${ss}px ${sc}`;
    }
    const card = (_jsxs("div", { style: cardStyle, children: [_jsxs("div", { style: { position: 'relative', display: 'flex' }, children: [_jsx("div", { style: { display: 'flex' }, children: _jsx(Stars, { fill: bgStar, outline: bgOutline }) }), _jsx("div", { style: {
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            overflow: 'hidden',
                            display: 'flex',
                            width: overlayWidth,
                        }, children: _jsx(Stars, { fill: star, outline: false }) })] }), label ? (_jsx("span", { style: {
                    marginInlineStart: '0.45em',
                    color: labelColor,
                    fontWeight: weight || undefined,
                }, children: label })) : null] }));
    /**
     * The breakdown card — only over a LIVE aggregate. `live` is `undefined` unless the app really counted
     * something for this product, so nothing here can invent a bar: under a number the seller typed, the
     * stars stay stars.
     */
    const breakdown = live !== undefined && bs.showBreakdown !== false;
    return (_jsxs(_Fragment, { children: [vis ? _jsx("style", { dangerouslySetInnerHTML: { __html: vis } }) : null, breakdown ? _jsx("style", { dangerouslySetInnerHTML: { __html: popCss(cls, star) } }) : null, _jsx("div", { className: cls, ...(scrollId ? { id: scrollId } : {}), style: {
                    display: 'flex',
                    justifyContent: justify,
                    fontSize: `${size}px`,
                    lineHeight: 1,
                    ...marginStyle(bs),
                }, children: breakdown && live ? (
                /*
                  A NATIVE `<details>`, not a React popover, and that is the whole point: the stars sit inside
                  the buy box, which hydrates as an island — for the couple of seconds before that island is
                  live (and forever, on a page whose script never arrives) a React `onClick` is a control that
                  looks clickable and does nothing. The browser opens a `<details>` with no script at all, on a
                  tap and on the keyboard, and closes it on a second tap. The button inside is an ordinary
                  anchor to the reviews section for exactly the same reason.
                */
                _jsxs("details", { ref: popRef, className: `${cls}__pop`, children: [_jsx("summary", { className: `${cls}__sum`, "aria-label": `${formatAverage(live.average, context.locale)}/5 — ${t(context, 'reviews.count', { count: live.count })}`, children: card }), _jsxs("div", { className: `${cls}__panel`, children: [_jsxs("div", { className: `${cls}__head`, children: [_jsx("b", { className: `${cls}__avg`, children: formatAverage(live.average, context.locale) }), _jsxs("div", { className: `${cls}__headr`, children: [_jsx("span", { className: `${cls}__pstars`, children: _jsxs("span", { style: { position: 'relative', display: 'flex' }, children: [_jsx("span", { style: { display: 'flex' }, children: _jsx(Stars, { fill: bgStar, outline: bgOutline }) }), _jsx("span", { style: {
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                    left: 0,
                                                                    overflow: 'hidden',
                                                                    display: 'flex',
                                                                    // The SAME clip as the stars above: when the note comes from the app,
                                                                    // `rating` IS `live.average`, so one computation serves both bars and they
                                                                    // can never disagree by a tenth.
                                                                    width: overlayWidth,
                                                                }, children: _jsx(Stars, { fill: star, outline: false }) })] }) }), _jsx("span", { className: `${cls}__pcount`, children: t(context, 'reviews.count', { count: live.count }) })] })] }), _jsx("div", { className: `${cls}__bars`, children: BARS.map((bar) => {
                                        const value = live.histogram[bar] ?? 0;
                                        return (_jsxs("div", { className: `${cls}__bar`, children: [_jsx("span", { className: `${cls}__barn`, children: formatCount(Number(bar), context.locale) }), _jsx("span", { className: `${cls}__bars-g`, "aria-hidden": "true", children: "\u2605" }), _jsx("span", { className: `${cls}__track`, "aria-hidden": "true", children: _jsx("span", { className: `${cls}__fill`, style: { inlineSize: `${barShare(value, live.count, live.histogram)}%` } }) }), _jsx("span", { className: `${cls}__barv`, children: formatCount(value, context.locale) })] }, bar));
                                    }) }), _jsxs("a", { className: `${cls}__cta`, href: `#${REVIEWS_ANCHOR_ID}`, children: [_jsx("span", { children: t(context, 'reviews.read_all', { count: live.count }) }), _jsx("span", { "aria-hidden": "true", children: "\u2197" })] })] })] })) : (card) })] }));
};
const BARS = ['5', '4', '3', '2', '1'];
/**
 * One bar's share of the TOTAL, never of the biggest bar. Two four-star reviews beside a thousand
 * five-star ones are a bar at nothing — scaling each bar against the tallest would draw them a third of
 * the way across and read as "a third of buyers gave four stars", which the number printed beside it
 * flatly contradicts.
 */
function barShare(value, count, histogram) {
    const total = count > 0 ? count : Object.values(histogram).reduce((a, b) => a + b, 0);
    if (total <= 0)
        return 0;
    return Math.round((value / total) * 100);
}
/**
 * The card's own stylesheet, scoped to this block's generated class so two rating blocks on one page
 * cannot style each other. Logical properties throughout: in an Arabic shop the card hangs from the
 * right, the bars fill right-to-left and the arrow sits under the right edge, with no second
 * implementation.
 */
function popCss(cls, star) {
    const c = `.${cls}`;
    return (`${c}__pop{position:relative;display:inline-block}` +
        // The native marker, gone in both engines — the disclosure triangle beside a star bar reads as a bug.
        `${c}__sum{list-style:none;cursor:pointer;display:inline-flex;align-items:center}` +
        `${c}__sum::-webkit-details-marker{display:none}` +
        `${c}__sum::marker{content:''}` +
        // 320px, or the screen minus its gutters — whichever is smaller. A fixed 320 on a 320px phone would
        // hang the card off the edge of the page.
        `${c}__panel{position:absolute;z-index:40;inset-block-start:calc(100% + 12px);inset-inline-start:0;` +
        `inline-size:min(320px,calc(100vw - 32px));box-sizing:border-box;padding:16px;` +
        `background:var(--aqx-scheme-bg,#fff);color:var(--aqx-scheme-text,#1a1a1a);` +
        `border:1px solid color-mix(in srgb,var(--aqx-scheme-text,#000) 12%,transparent);` +
        `border-radius:14px;box-shadow:0 18px 40px -18px rgba(0,0,0,.45);` +
        `font-size:13px;line-height:1.3;text-align:start;cursor:default}` +
        // The little beak under the stars, drawn as a rotated corner of the card itself so it carries the
        // same border and the same fill whatever the scheme paints them.
        // Its POSITION is logical (it follows the stars into the right-hand corner in an Arabic shop) but the
        // two edges it strokes are PHYSICAL top + left, and that distinction is the whole beak: a rotation is
        // not mirrored by `dir`, so `border-inline-start` stroked the two edges facing DOWN in Arabic and drew
        // a stray chevron hanging off the corner (caught in the RTL screenshot, 16/09/2026).
        `${c}__panel::before{content:'';position:absolute;inset-block-start:-7px;inset-inline-start:22px;` +
        `inline-size:12px;block-size:12px;background:inherit;` +
        `border-left:1px solid color-mix(in srgb,var(--aqx-scheme-text,#000) 12%,transparent);` +
        `border-top:1px solid color-mix(in srgb,var(--aqx-scheme-text,#000) 12%,transparent);` +
        `transform:rotate(45deg)}` +
        `${c}__head{display:flex;align-items:center;gap:12px}` +
        `${c}__avg{font-size:34px;font-weight:700;line-height:1;letter-spacing:-.02em}` +
        `${c}__headr{display:flex;flex-direction:column;gap:5px}` +
        `${c}__pstars{display:inline-flex;font-size:15px;line-height:1}` +
        `${c}__pcount{font-size:12px;color:color-mix(in srgb,var(--aqx-scheme-text,#000) 62%,transparent)}` +
        `${c}__bars{display:flex;flex-direction:column;gap:5px;margin-block:14px}` +
        `${c}__bar{display:grid;grid-template-columns:auto auto 1fr auto;align-items:center;gap:8px}` +
        `${c}__barn{font-size:12px;font-variant-numeric:tabular-nums}` +
        `${c}__bars-g{color:${star};font-size:12px;line-height:1}` +
        `${c}__track{block-size:8px;border-radius:99px;` +
        `background:color-mix(in srgb,var(--aqx-scheme-text,#000) 10%,transparent);overflow:hidden}` +
        `${c}__fill{display:block;block-size:100%;background:${star}}` +
        `${c}__barv{font-size:12px;font-variant-numeric:tabular-nums;` +
        `color:color-mix(in srgb,var(--aqx-scheme-text,#000) 62%,transparent)}` +
        // The button the founder asked for: dark, full width, its label in small caps with the same outward
        // arrow her reference draws.
        `${c}__cta{display:flex;align-items:center;justify-content:space-between;gap:10px;` +
        `padding:13px 15px;border-radius:8px;text-decoration:none;` +
        `background:var(--aqx-scheme-text,#3f3f3f);color:var(--aqx-scheme-bg,#fff);` +
        `font-size:12px;font-weight:600;letter-spacing:.06em;text-transform:uppercase}` +
        `${c}__cta:hover{opacity:.9}`);
}
function resolveHex(v, fallback) {
    if (v && typeof v === 'object') {
        const c = v;
        if (typeof c.value === 'string')
            return c.value;
        // 0019: theme-token branch → live var so a re-theme reflows (allowlisted, finding #18).
        if (c.ref === 'theme' && typeof c.token === 'string' && isThemeColorToken(c.token))
            return `var(--aqx-theme-${c.token})`;
    }
    if (typeof v === 'string')
        return v;
    return fallback;
}
export const ratingBlock = {
    type: 'rating',
    schema: ratingBlockSchema,
    Component: RatingBlock,
    meta: { label: 'Rating stars' },
};
//# sourceMappingURL=index.js.map